import { Node } from '@tiptap/core';
import type { Node as PMNode } from '@tiptap/pm/model';
import { normalizeAudioType, type AudioType } from '../../../shared/audioTypes';
import { normalizeVisualType, type VisualType } from '../../../shared/visualTypes';
import {
  AUDIO_CHIP_CONFIG,
  VISUAL_CHIP_CONFIG,
  mountCellTypeChip,
} from './CellTypeChip';

export const ScriptCell = Node.create({
  name: 'scriptCell',
  // Reference images (2B) sit after the text, Visual cells only; see RefImage.ts.
  content: 'paragraph+ refImage*',
  isolating: true,
  defining: true,

  addAttributes() {
    return {
      kind: {
        default: 'audio',
        parseHTML: (el) => el.getAttribute('data-kind') ?? 'audio',
        renderHTML: (attrs: { kind?: string }) => ({ 'data-kind': attrs.kind ?? 'audio' }),
      },
      // Audio-type label (voiceover / to_camera / interview / silent). Only
      // meaningful on AUDIO cells. `null` = untyped (legacy rows / not yet
      // tagged) — see src/shared/audioTypes.ts. Rides inside doc_json, so it
      // reaches the client review page, the PDF and the diff with no extra
      // plumbing.
      audioType: {
        default: null,
        parseHTML: (el) => normalizeAudioType(el.getAttribute('data-audio-type')),
        renderHTML: (attrs: { audioType?: unknown }) => {
          const at = normalizeAudioType(attrs.audioType);
          return at ? { 'data-audio-type': at } : {};
        },
      },
      // Only meaningful on VISUAL cells. `null` = untyped (legacy rows / not
      // yet tagged), as described in src/shared/visualTypes.ts.
      visualType: {
        default: null,
        parseHTML: (el) => normalizeVisualType(el.getAttribute('data-visual-type')),
        renderHTML: (attrs: { visualType?: unknown }) => {
          const vt = normalizeVisualType(attrs.visualType);
          return vt ? { 'data-visual-type': vt } : {};
        },
      },
    };
  },

  parseHTML() {
    return [{ tag: 'div[data-type="script-cell"]' }];
  },

  renderHTML({ HTMLAttributes }) {
    const kind = HTMLAttributes['data-kind'] ?? 'audio';
    return [
      'div',
      {
        'data-type': 'script-cell',
        class: `script-cell script-cell-${kind}`,
        ...HTMLAttributes,
      },
      0,
    ];
  },

  /**
   * NodeView renders the cell wrapper plus the cell-type chip that sits above
   * the text: the audio-type chip on AUDIO cells, the visual-type chip on
   * VISUAL cells. The chip reflects the cell's `audioType` / `visualType`
   * attribute and, in the editable editor, opens the picker to change it.
   *
   * Used in both the admin (editable) and client-review (read-only) editors,
   * since both mount the same extension set.
   */
  addNodeView() {
    return ({ node, getPos, editor }) => {
      let current: PMNode = node;
      const kind = current.attrs.kind === 'visuals' ? 'visuals' : 'audio';

      const dom = document.createElement('div');
      dom.setAttribute('data-type', 'script-cell');
      // Base classes are set once; `kind` never changes (update() returns false
      // on a kind change), so we never rewrite className — that would clobber
      // any class ProseMirror manages on the node's DOM (e.g. selectednode).
      dom.className = `script-cell script-cell-${kind}`;

      const contentDOM = document.createElement('div');
      contentDOM.className = 'script-cell-content';

      // Each kind owns one attribute and one data-* mirror on `dom`.
      const readType = (n: PMNode): string | null =>
        kind === 'audio'
          ? normalizeAudioType(n.attrs.audioType)
          : normalizeVisualType(n.attrs.visualType);
      const dataAttr = kind === 'audio' ? 'data-audio-type' : 'data-visual-type';

      // Only the attribute we own is touched per update; PM/decoration classes
      // on `dom` survive.
      const syncType = (n: PMNode) => {
        const t = readType(n);
        if (t) dom.setAttribute(dataAttr, t);
        else dom.removeAttribute(dataAttr);
      };
      syncType(current);

      // One setNodeMarkup transaction, so undo reverts the change in one step.
      const commit = (attrs: Record<string, unknown>) => {
        const pos = typeof getPos === 'function' ? getPos() : null;
        if (typeof pos !== 'number') return;
        editor.view.dispatch(
          editor.state.tr.setNodeMarkup(pos, undefined, attrs),
        );
      };
      const editable = editor.isEditable;
      const getAudio = () => normalizeAudioType(current.attrs.audioType);
      const getVisual = () => normalizeVisualType(current.attrs.visualType);
      let chip: { el: HTMLElement | null; destroy: () => void };
      let refreshChip: () => void;
      if (kind === 'audio') {
        const h = mountCellTypeChip<AudioType>(AUDIO_CHIP_CONFIG, {
          editable,
          getType: getAudio,
          onSelect: (type: AudioType) => commit({ ...current.attrs, audioType: type }),
        });
        refreshChip = () => h.update(getAudio());
        chip = h;
      } else {
        const h = mountCellTypeChip<VisualType>(VISUAL_CHIP_CONFIG, {
          editable,
          getType: getVisual,
          onSelect: (type: VisualType) => commit({ ...current.attrs, visualType: type }),
        });
        refreshChip = () => h.update(getVisual());
        chip = h;
      }
      if (chip.el) dom.appendChild(chip.el);

      dom.appendChild(contentDOM);

      return {
        dom,
        contentDOM,
        update(updated: PMNode) {
          if (updated.type.name !== 'scriptCell') return false;
          const nextKind = updated.attrs.kind === 'visuals' ? 'visuals' : 'audio';
          if (nextKind !== kind) return false; // kind change → rebuild the view
          // A read-only cell renders a chip only when it's typed (untyped ones
          // keep the bare "A" / "V"). The chip handle is built once, so if that
          // typed↔untyped state would flip, rebuild the view rather than leave
          // a stale handle. (Editable cells always have a chip, so they're fine
          // to update in place.)
          if (!editor.isEditable) {
            const had = readType(current) != null;
            const has = readType(updated) != null;
            if (had !== has) return false;
          }
          current = updated;
          syncType(updated);
          refreshChip();
          return true;
        },
        // The chip lives outside contentDOM; its mutations are ours to manage,
        // so hide them from ProseMirror. Anything inside contentDOM (the
        // editable paragraphs) is handled normally. Node.contains is true for
        // the node itself, so this also lets childList changes on contentDOM
        // through.
        ignoreMutation(mutation: MutationRecord | { type: 'selection' }) {
          if (mutation.type === 'selection') return false;
          return !contentDOM.contains((mutation as MutationRecord).target);
        },
        destroy() {
          chip.destroy();
        },
      };
    };
  },
});
